import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { User, LoginRequest, LoginResponse } from '../types';
import { authApi } from '../../modules/auth/services/authApi';
import { useAppStore } from '../stores/useAppStore';
import { canonicalizeRole, canonicalizeRoles } from '../rbac/roles';
import { getQueryClient } from '../services/queryClientRef';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRestricted: boolean;
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  /**
   * Adota o token que o backend acabou de emitir, sem novo login.
   *
   * Existe por UM caminho: a troca de senha obrigatória. O janus-auth emite
   * ali um par novo ("Issue new full JWT", `registration_service.py`) porque
   * o token do login carrega `restricted: true` — e o BFF BLOQUEIA tudo
   * menos seis rotas enquanto essa claim existir. Descartar o token novo
   * fazia o usuário entrar no app logo depois de trocar a senha e tomar 403
   * "Password change required" em toda chamada, até o access token expirar.
   */
  adotarToken: (accessToken: string) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Normalize a User payload coming from the BFF/JWT so role / roles are always
 * canonical client-side (Wave 3 RBAC sanitization).
 *
 * Falls back to the original strings when the value isn't recognized so we
 * never accidentally drop information from a forward-compatible backend.
 */
function canonicalizeUserRoles(user: User): User {
  if (!user) return user;
  const next: Record<string, unknown> = { ...(user as unknown as Record<string, unknown>) };
  if (typeof user.role === 'string') {
    next.role = canonicalizeRole(user.role) ?? user.role;
  }
  const rawRoles = (user as unknown as Record<string, unknown>).roles;
  if (Array.isArray(rawRoles)) {
    const canonical = canonicalizeRoles(rawRoles as string[]);
    next.roles = canonical.length > 0 ? canonical : rawRoles;
  }
  return next as unknown as User;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isRestricted, setIsRestricted] = useState(false);
  const { currentUser, setCurrentUser } = useAppStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathnameRef = React.useRef(pathname);
  pathnameRef.current = pathname;

  const validateSession = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setCurrentUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const rawUser = await authApi.getMe();
      const user = canonicalizeUserRoles(rawUser);
      localStorage.setItem('user', JSON.stringify(user));
      const activeTenant = user.tenantId ?? (user as unknown as Record<string, unknown>).tenant_id;
      if (activeTenant) {
        localStorage.setItem('janus.activeTenant', String(activeTenant));
      }
      setCurrentUser(user);
      // O dispositivo tem credencial propria; validar a sessao Janus nao deve
      // tirar sua pagina publica. Use a rota atual apos a resposta assincrona.
      const isDevicePage = pathnameRef.current === '/meet/tp' || pathnameRef.current === '/meet/tp/';
      // Restricted JWT: redirect to change-password
      if (user.restricted) {
        setIsRestricted(true);
        if (!isDevicePage) navigate('/change-password', { replace: true });
        setIsLoading(false);
        return;
      }
      // Redirect first-login users to onboarding interview when applicable.
      // Skipped when the user has explicitly clicked "Sair para o dashboard"
      // during this tab session (see InterviewPage). Without this guard, every
      // re-validation would yank the user back to /onboarding/interview as
      // long as their server-side status stays 'in_progress' / 'paused'.
      const userDismissedOnboarding =
        typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem('janus_onboarding_dismissed') === '1';
      if (user.firstLogin && !user.onboardingCompletedAt && !userDismissedOnboarding && !isDevicePage) {
        const interviewStatus = user.onboardingInterviewStatus;
        if (!interviewStatus || interviewStatus === 'pending' || interviewStatus === 'in_progress' || interviewStatus === 'paused') {
          navigate('/onboarding/interview', { replace: true });
        } else {
          navigate('/onboarding', { replace: true });
        }
      }
    } catch (err: unknown) {
      console.error('[AuthContext] Session validation failed:', err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      // Só desloga se o backend explicitamente recusou o token (401 / 403).
      // Timeout de cold start, 502/503/504 ou oscilação de rede NÃO devem derrubar a sessão:
      // ignorância nunca vira veredito.
      if (status === 401 || status === 403) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        localStorage.removeItem('janus.activeTenant');
        setCurrentUser(null);
      } else {
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          try {
            setCurrentUser(canonicalizeUserRoles(JSON.parse(cachedUser)));
          } catch {
            // ignore
          }
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [setCurrentUser, navigate]);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  /**
   * O token novo entra no lugar do antigo e a restrição CAI junto.
   *
   * As duas coisas no mesmo gesto de propósito: guardar o token e deixar
   * `isRestricted` verdadeiro manteria o `RestrictedRoute` empurrando de
   * volta para a troca de senha, sobre uma senha que já foi trocada.
   */
  const adotarToken = (accessToken: string) => {
    localStorage.setItem('accessToken', accessToken);
    setIsRestricted(false);
  };

  const login = async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials);
    const canonicalUser = canonicalizeUserRoles(response.user);
    const canonicalResponse: LoginResponse = { ...response, user: canonicalUser };
    localStorage.setItem('accessToken', canonicalResponse.accessToken);
    localStorage.setItem('user', JSON.stringify(canonicalResponse.user));
    const activeTenant = canonicalResponse.user.tenantId ?? (canonicalResponse.user as unknown as Record<string, unknown>).tenant_id;
    if (activeTenant) {
      localStorage.setItem('janus.activeTenant', String(activeTenant));
    }
    setCurrentUser(canonicalResponse.user);

    // Check for restricted JWT (must change password)
    if (canonicalResponse.must_change_password || canonicalResponse.restricted) {
      setIsRestricted(true);
      navigate('/change-password', { replace: true });
      return canonicalResponse;
    }

    // Redirect first-login users to onboarding interview when applicable
    if (canonicalResponse.user.firstLogin && !canonicalResponse.user.onboardingCompletedAt) {
      const interviewStatus = canonicalResponse.user.onboardingInterviewStatus;
      if (!interviewStatus || interviewStatus === 'pending' || interviewStatus === 'in_progress' || interviewStatus === 'paused') {
        navigate('/onboarding/interview', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    }

    return canonicalResponse;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('janus.activeTenant');
      try {
        sessionStorage.removeItem('janus_onboarding_dismissed');
      } catch {
        // ignore
      }
      try {
        getQueryClient()?.clear();
      } catch {
        // ignore
      }
      setCurrentUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: currentUser,
        isAuthenticated: !!currentUser,
        isLoading,
        isRestricted,
        adotarToken,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType;
export function useAuth(optional: true): AuthContextType | undefined;
export function useAuth(optional?: true): AuthContextType | undefined {
  const context = useContext(AuthContext);
  if (context === undefined && !optional) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
